"use client";

import {ChevronUp, CircleHelp, Info, Mail, Moon, Sun, Settings, LogOut} from "lucide-react";
import {useTheme} from "next-themes";
import {forwardRef, type ComponentPropsWithoutRef, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Link from "next/link";
import {useAuth} from "@/app/auth/hooks";
import {LocalizationKeys} from "@/lib/all-localization-keys";
import type {ClientSessionData} from "@/app/auth/session-types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {GradientAvatar} from "@/components/ui/gradient-avatar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {UserRoleDialog} from "@/components/user-role-dialog";
import {DisclaimerDialog} from "@/components/disclaimer-dialog";
import {
  useUserRole,
  useUserRoleOption,
} from "@/components/user-role-switcher";
import {UserRole} from "@/lib/types";
import {cn} from "@/lib/utils";
import {useIsMobile} from "@/hooks/use-mobile";
import {useDisclaimer} from "@/hooks/use-disclaimer";
import {LoaderIcon} from "./icons";
import {toast} from "./toast";
import {AccountSettingsDialog} from "./account-settings-dialog";
import {basePath} from "@/lib/config";

type OriHelpLinkProps = ComponentPropsWithoutRef<"a"> & {
  iconClassName?: string;
};

const OriHelpLink = forwardRef<HTMLAnchorElement, OriHelpLinkProps>(
  ({iconClassName, ...props}, ref) => {
    const {t} = useTranslation();

    return (
      <a
        {...props}
        data-testid="user-nav-item-ori-help"
        href={`${basePath}/help/`}
        ref={ref}
        rel="noopener noreferrer"
        target="_blank"
      >
        <CircleHelp className={cn("h-4 w-4", iconClassName)}/>
        {t(LocalizationKeys.ORI_HELP_LINK)}
      </a>
    );
  }
);

OriHelpLink.displayName = "OriHelpLink";

export function SidebarUserNav({user}: { user: ClientSessionData["user"] }) {
  const {t} = useTranslation();
  const {status, signOut} = useAuth();
  const {setTheme, resolvedTheme} = useTheme();
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDisclaimerDialog, setShowDisclaimerDialog] = useState(false);
  const userRole = useUserRole();
  const isMobile = useIsMobile();
  const {
    isAcknowledged: isDisclaimerAcknowledged,
    isLoading: isDisclaimerLoading,
    isAcknowledging,
    content: disclaimerContent,
    acknowledge: acknowledgeDisclaimer,
  } = useDisclaimer();

  // Get the role text for the menu item
  const roleText = useUserRoleOption({
    [UserRole.ALPHA]: () => "🧪 " + t(LocalizationKeys.ACCOUNT_ROLE_ALPHA),
    [UserRole.BETA]: () => "🧪 " + t(LocalizationKeys.ACCOUNT_ROLE_BETA),
    [UserRole.DEFAULT_USER]: () => null,
    unauthenticated: () => null,
  })();

  // Show disclaimer dialog on first sign-in if not acknowledged
  useEffect(() => {
    if (status !== "authenticated" || isDisclaimerLoading) {
      return;
    }

    if (!isDisclaimerAcknowledged) {
      setShowDisclaimerDialog(true);
    }
  }, [status, isDisclaimerAcknowledged, isDisclaimerLoading]);

  // Show role dialog on first sign-in for alpha/beta users
  useEffect(() => {
    if (status !== "authenticated") {
      return;
    }

    if (userRole !== UserRole.ALPHA && userRole !== UserRole.BETA) {
      return;
    }

    const localStorageKey = `${userRole}_intro_modal_was_seen`;
    const hasSeenModal = localStorage.getItem(localStorageKey);

    if (!hasSeenModal) {
      setShowRoleDialog(true);
    }
  }, [status, userRole]);

  const handleSignOut = () => {
    if (status === "loading") {
      toast({
        type: "error",
        description: t(LocalizationKeys.ERROR_AUTH_STATUS),
      });
    } else {
      signOut();
    }
  };

  const handleRoleClick = () => {
    setShowUserMenu(false);
    setShowRoleDialog(true);
  };

  const handleDisclaimerClick = () => {
    setShowUserMenu(false);
    setShowDisclaimerDialog(true);
  };

  const handleThemeToggle = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  const handleRoleDialogChange = (open: boolean) => {
    // When closing the dialog (open becomes false), mark it as seen
    if (!open && userRole) {
      const localStorageKey = `${userRole}_intro_modal_was_seen`;
      localStorage.setItem(localStorageKey, "true");
    }
    setShowRoleDialog(open);
  };

  // User button content (same for both mobile and desktop)
  const userButtonContent = status === "loading" ? (
    <>
      <div className="size-6 animate-pulse rounded-full bg-muted"/>
      <span className="animate-pulse rounded-md bg-muted text-transparent">
        {t(LocalizationKeys.STATUS_LOADING)}
      </span>
      <div className="ml-auto animate-spin text-muted-foreground">
        <LoaderIcon/>
      </div>
    </>
  ) : (
    <>
      <GradientAvatar
        alt={user.email ?? "User Avatar"}
        identifier={user.email ?? "default"}
        size={24}
      />
      <span className="truncate text-sm" data-testid="user-email">
        {user.email}
      </span>
      <ChevronUp className="ml-auto"/>
    </>
  );

  // Menu items content (same for both mobile and desktop)
  const menuItems = (
    <>
      {roleText && (
        <>
          <button
            className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
            data-testid="user-role"
            onClick={handleRoleClick}
            type="button"
          >
            {roleText}
          </button>
          <div className="-mx-1 my-1 h-px bg-muted"/>
        </>
      )}
      <button
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
        data-testid="user-nav-item-information"
        onClick={handleDisclaimerClick}
        type="button"
      >
        <Info className="h-4 w-4"/>
        {t(LocalizationKeys.BUTTON_INFORMATION)}
      </button>
      <div className="-mx-1 my-1 h-px bg-muted"/>
      <OriHelpLink
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
      />
      <div className="-mx-1 my-1 h-px bg-muted"/>
      <Link
        href={`/support`}
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
        data-testid="user-nav-item-support"
      >
        <Mail className="h-4 w-4"/>
        {t(LocalizationKeys.SUPPORT_CONTACT_LINK)}
      </Link>
      <div className="-mx-1 my-1 h-px bg-muted"/>
      <button
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
        data-testid="user-nav-item-theme"
        onClick={handleThemeToggle}
        type="button"
      >
        {resolvedTheme === 'dark' ? <Sun className="h-4 w-4"/> : <Moon className="h-4 w-4"/>}
        {t(LocalizationKeys.BUTTON_TOGGLE_THEME, {mode: resolvedTheme === 'dark' ? 'light' : 'dark'})}
      </button>
      <div className="-mx-1 my-1 h-px bg-muted"/>
      <AccountSettingsDialog user={user}>
        <button
          className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
          data-testid="user-nav-item-account-settings"
          type="button"
        >
          <Settings className="h-4 w-4"/>
          {t(LocalizationKeys.ACCOUNT_SETTINGS)}
        </button>
      </AccountSettingsDialog>
      <div className="-mx-1 my-1 h-px bg-muted"/>
      <button
        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
        data-testid="user-nav-item-auth"
        onClick={handleSignOut}
        type="button"
      >
        <LogOut className="h-4 w-4"/>
        {t(LocalizationKeys.BUTTON_SIGN_OUT)}
      </button>
    </>
  );

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          {isMobile ? (
            <>
              <SidebarMenuButton
                className="h-auto min-h-10 bg-background"
                data-testid="user-nav-button"
                onClick={() => setShowUserMenu(true)}
              >
                {userButtonContent}
              </SidebarMenuButton>
              <Sheet onOpenChange={setShowUserMenu} open={showUserMenu}>
                <SheetContent side="bottom" className="p-0">
                  <SheetHeader className="flex-row items-center gap-3 space-y-0 border-b p-4">
                    <GradientAvatar
                      alt={user.email ?? "User Avatar"}
                      identifier={user.email ?? "default"}
                      size={28}
                    />
                    <SheetTitle className="text-sm font-normal leading-none">
                      {user.email}
                    </SheetTitle>
                  </SheetHeader>
                  <SheetDescription className="sr-only">
                    {t(LocalizationKeys.ACCOUNT_SETTINGS_DESCRIPTION)}
                  </SheetDescription>
                  <div className="flex flex-col gap-1 p-4 pb-8" data-testid="user-nav-menu">
                    {menuItems}
                  </div>
                </SheetContent>
              </Sheet>
            </>
          ) : (
            <DropdownMenu modal>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  className="h-auto min-h-10 bg-background data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  data-testid="user-nav-button"
                >
                  {userButtonContent}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="center"
                className="md:w-auto md:min-w-56"
                data-testid="user-nav-menu"
                side="top"
                sideOffset={8}
              >
                {roleText && (
                  <>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      data-testid="user-role"
                      onSelect={handleRoleClick}
                    >
                      {roleText}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator/>
                  </>
                )}
                <DropdownMenuItem
                  className="cursor-pointer"
                  data-testid="user-nav-item-information"
                  onSelect={handleDisclaimerClick}
                >
                  <Info className="mr-2 h-4 w-4"/>
                  {t(LocalizationKeys.BUTTON_INFORMATION)}
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem asChild>
                  <OriHelpLink
                    className="cursor-pointer"
                    iconClassName="mr-2"
                  />
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem asChild data-testid="user-nav-item-support">
                  <Link
                    href={`/support`}
                    className="cursor-pointer"
                  >
                    <Mail className="mr-2 h-4 w-4"/>
                    {t(LocalizationKeys.SUPPORT_CONTACT_LINK)}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem
                  className="cursor-pointer"
                  data-testid="user-nav-item-theme"
                  onSelect={handleThemeToggle}
                >
                  {resolvedTheme === 'dark' ? <Sun className="mr-2 h-4 w-4"/> : <Moon className="mr-2 h-4 w-4"/>}
                  {t(LocalizationKeys.BUTTON_TOGGLE_THEME, {mode: resolvedTheme === 'dark' ? 'light' : 'dark'})}
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <AccountSettingsDialog user={user}>
                  <DropdownMenuItem
                    data-testid="user-nav-item-account-settings"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <Settings className="mr-2 h-4 w-4"/>
                    {t(LocalizationKeys.ACCOUNT_SETTINGS)}
                  </DropdownMenuItem>
                </AccountSettingsDialog>
                <DropdownMenuSeparator/>
                <DropdownMenuItem asChild data-testid="user-nav-item-auth">
                  <button
                    className="w-full cursor-pointer"
                    onClick={handleSignOut}
                    type="button"
                  >
                    <LogOut className="mr-2 h-4 w-4"/>
                    {t(LocalizationKeys.BUTTON_SIGN_OUT)}
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </SidebarMenuItem>
      </SidebarMenu>

      <UserRoleDialog onOpenChange={handleRoleDialogChange} open={showRoleDialog}/>
      <DisclaimerDialog
        content={disclaimerContent}
        isAcknowledged={isDisclaimerAcknowledged}
        isAcknowledging={isAcknowledging}
        onAcknowledge={acknowledgeDisclaimer}
        onOpenChange={setShowDisclaimerDialog}
        open={showDisclaimerDialog}
      />
    </>
  );
}
